import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/Button';
import { CircularLoader } from '@/components/ui/CircularLoader';
import { ArrowRight, Eye, EyeOff, Info } from '@/components/ui/icons/Icons';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useI18n } from '@/context/I18nProvider';
import { getClerkErrorMessage } from '@/utils/clerk-errors';
import { getLastAuthMethod, setLastAuthMethod } from '@/utils/last-auth-method';
import { GoogleOneTap, useAuth, useSignIn, useUser } from '@clerk/nextjs';
import { OAuthStrategy } from '@clerk/types';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);

export default function SignInPage() {
  const { t } = useI18n();
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isSignedIn, isLoaded: isAuthLoaded } = useAuth();
  const { isLoaded: isUserLoaded, user } = useUser();
  const router = useRouter();

  // Invitation flow: detect __clerk_ticket from Clerk invitation email links.
  // Existing users who click an invitation link may land on sign-in.
  const ticket = router.query.__clerk_ticket as string | undefined;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [lastAuthMethod, setLastAuthMethodState] = useState<string | null>(null);
  const ticketSignInAttemptedRef = useRef(false);
  // Invited org info (fetched from ticket JWT via API)
  const [invitedOrgName, setInvitedOrgName] = useState<string | null>(null);
  const [isLoadingInvitedOrg, setIsLoadingInvitedOrg] = useState(false);
  // Prevent GoogleOneTap from auto-firing after a failed OAuth redirect loop
  const [suppressOneTap, setSuppressOneTap] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem('orsay_oauth_fallback')) {
        setSuppressOneTap(true);
        sessionStorage.removeItem('orsay_oauth_fallback');
      }
    } catch {}
  }, []);

  // Fetch invited org name from ticket
  useEffect(() => {
    if (!ticket) return;
    setIsLoadingInvitedOrg(true);
    fetch(`/api/clerk/invitation-org?ticket=${encodeURIComponent(ticket)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (data.name) setInvitedOrgName(data.name);
      })
      .catch(() => {}) // Ignore — generic message will be shown
      .finally(() => setIsLoadingInvitedOrg(false));
  }, [ticket]);

  // Load last auth method from localStorage
  useEffect(() => {
    setLastAuthMethodState(getLastAuthMethod());
  }, []);

  // Auto sign-in with invitation ticket if present (existing user clicking invite link)
  useEffect(() => {
    if (!isLoaded || !signIn || !ticket || ticketSignInAttemptedRef.current) return;
    ticketSignInAttemptedRef.current = true;

    const signInWithTicket = async () => {
      try {
        setIsLoading(true);
        const result = await signIn.create({
          strategy: 'ticket',
          ticket,
        });
        if (result.status === 'complete' && result.createdSessionId) {
          setLastAuthMethod('email');
          // Decode org ID from the ticket JWT so we can set it as active.
          // This prevents the "ghost org" bug where last_active_organization_id
          // stays null even though the invitation was accepted.
          let orgId: string | undefined;
          try {
            const parts = ticket.split('.');
            const payload = JSON.parse(atob(parts[1]));
            orgId = payload.oid; // Clerk org ID
          } catch {
            // Malformed token — setActive without org, OrganizationGuard will recover
          }
          await setActive({
            session: result.createdSessionId,
            organization: orgId,
          });
          setIsRedirecting(true);
          // Hard redirect to ensure session cookie propagates to the server middleware.
          // router.replace can race with cookie propagation, causing a bounce to /sign-in.
          window.location.href = '/conversations';
        }
      } catch {
        // Ticket invalid or expired — fall back to normal sign-in form
        ticketSignInAttemptedRef.current = true;
      } finally {
        setIsLoading(false);
      }
    };

    signInWithTicket();
  }, [isLoaded, signIn, ticket, setActive, router]);

  // Validate form reactively
  const isFormValid = useMemo(() => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    return email.trim() !== '' && password.trim() !== '' && isValidEmail;
  }, [email, password]);

  // Redirect to /conversations if already signed in.
  // When a ticket is present (invitation flow), accept any pending org invitations first
  // (e.g., Google OAuth sign-in where the ticket wasn't consumed through the OAuth flow).
  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn || isRedirecting) return;

    // If a ticket is present, wait for user to load so we can accept pending invitations
    if (ticket && isUserLoaded && user) {
      setIsRedirecting(true);
      (async () => {
        try {
          const invitations = await user.getOrganizationInvitations({ status: 'pending' });
          if (invitations.data.length > 0) {
            const accepted = await invitations.data[0].accept();
            await setActive?.({ organization: accepted.publicOrganizationData.id });
          }
        } catch {
          // Ignore — OrganizationGuard will handle as fallback
        }
        window.location.href = '/conversations';
      })();
      return;
    }

    // No ticket — redirect immediately
    if (!ticket) {
      setIsRedirecting(true);
      // Respect Clerk's redirect_url (e.g. OAuth consent flow) if present,
      // but only for trusted Clerk account portal domains to prevent open redirects.
      const redirectUrl = new URLSearchParams(window.location.search).get('redirect_url');
      let target = '/conversations';
      if (redirectUrl) {
        try {
          const url = new URL(redirectUrl);
          if (
            url.hostname.endsWith('.clerk.accounts.dev') ||
            url.hostname === 'accounts.app.orsay.ai'
          ) {
            target = redirectUrl;
          }
        } catch {
          // Invalid URL — fall through to /conversations
        }
      }
      window.location.href = target;
    }
  }, [isAuthLoaded, isSignedIn, isRedirecting, ticket, isUserLoaded, user, setActive]);

  const handleOAuthSignIn = async (strategy: OAuthStrategy) => {
    if (!isLoaded || !signIn) return;
    setIsGoogleLoading(true);
    try {
      // Store that Google was used
      setLastAuthMethod('google');
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/conversations',
      });
    } catch (err: unknown) {
      setError(getClerkErrorMessage(err, t));
      setIsGoogleLoading(false);
    }
  };

  const handleEmailPasswordSignIn = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await signIn.create({
        identifier: email,
        password: password,
      });

      if (result.status === 'complete' && result.createdSessionId) {
        // Store that email/password was used
        setLastAuthMethod('email');
        await setActive({ session: result.createdSessionId });
        setIsRedirecting(true);
        window.location.href = '/conversations';
      }
    } catch (err: unknown) {
      const clerkError = err as { errors?: { code?: string }[] };
      if (clerkError.errors?.[0]?.code === 'session_exists') {
        window.location.href = '/conversations';
        return;
      }
      setError(getClerkErrorMessage(err, t));
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while redirecting
  if (isRedirecting || (isAuthLoaded && isSignedIn)) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <CircularLoader />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {!suppressOneTap && <GoogleOneTap signInForceRedirectUrl="/conversations" />}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col items-center space-y-3">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">
            {t('auth.welcomeTo')} <span className="text-primary">Orsay</span>
          </h1>
          <p className="text-gray-500 text-sm text-center">{t('auth.subtitle')}</p>
        </div>

        {/* Invitation info banner */}
        {ticket && (
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <Info className="size-5 shrink-0 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">
                {isLoadingInvitedOrg
                  ? t('auth.loadingOrgInfo')
                  : invitedOrgName
                    ? t('auth.invitedToOrg', { orgName: invitedOrgName })
                    : t('auth.invitedToOrgGeneric')}
              </p>
              <p className="mt-1 text-blue-600">{t('auth.invitedSignInHint')}</p>
            </div>
          </div>
        )}

        <div className="relative">
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 rounded-full border-2 border-gray-200 text-sm font-medium gap-3 hover:bg-gray-50 relative disabled:bg-white disabled:text-gray-500 disabled:border-gray-200 disabled:opacity-70"
            onClick={() => handleOAuthSignIn('oauth_google')}
            disabled={isGoogleLoading}
          >
            {isGoogleLoading ? <CircularLoader className="size-4" /> : <GoogleIcon />}
            {t('auth.continueWithGoogle')}
            {lastAuthMethod === 'google' && (
              <span className="absolute -top-2 -right-3 px-2.5 py-1 bg-white border border-gray-200 text-blue-600 text-xs font-medium rounded-full shadow-sm">
                {t('auth.lastUsed')}
              </span>
            )}
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-4 text-muted-foreground font-medium">
              {t('auth.orContinueWithEmail')}
            </span>
          </div>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleEmailPasswordSignIn} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <div className="relative">
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                className="h-12 rounded-lg border-gray-300 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                autoComplete="email"
                required
              />
              {lastAuthMethod === 'email' && (
                <span className="absolute -top-2 -right-3 px-2.5 py-1 bg-white border border-gray-200 text-blue-600 text-xs font-medium rounded-full shadow-sm z-10">
                  {t('auth.lastUsed')}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Link href="/forgot-password" className="text-xs text-primary hover:underline">
                {t('auth.forgotPassword')}
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.passwordPlaceholder')}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                className="h-12 rounded-lg pr-10 border-gray-300 bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-sm text-destructive text-center bg-destructive/10 rounded-lg p-3 break-words">
              {error}
            </div>
          )}

          {/* Clerk Smart CAPTCHA widget mount point for bot protection */}
          <div id="clerk-captcha" className="mt-2 flex justify-center" />

          <Button
            type="submit"
            className="w-full h-12 rounded-full text-sm font-medium disabled:bg-primary disabled:text-white disabled:opacity-50"
            disabled={isLoading || !isFormValid}
          >
            {isLoading ? (
              <CircularLoader className="size-4" />
            ) : (
              <>
                {t('auth.signIn')}
                <ArrowRight className="size-4" />
              </>
            )}
          </Button>
        </form>

        {/* Sign up link — preserve invitation ticket if present */}
        <p className="text-center text-sm text-muted-foreground">
          {t('auth.noAccount')}{' '}
          <Link
            href={ticket ? `/sign-up?__clerk_ticket=${ticket}` : '/sign-up'}
            className="text-primary font-medium hover:underline"
          >
            {t('auth.signUp')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
